import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import ProductPicker, { CategoryPicker } from '../../components/admin/ProductPicker';
import {
  API, withCreds, inputCls, selectCls, btnPrimary, btnSecondary, Field, Modal, PageHeader, Spinner,
  ScheduleBadge, discountLabel, fmtDateTime, toLocalInput, fromLocalInput, errMsg,
} from '../../components/admin/AdminUI';
import { Plus, Edit, Trash2, Tag, Megaphone, Shuffle } from 'lucide-react';
import Select from '../../components/FlatSelect';

const randomCode = () => Array.from({ length: 8 }, () => 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)]).join('');

function CouponModal({ coupon, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: '', description: '', type: 'percent', value: 10, min_order: 0, max_uses: null, starts_at: null, expires_at: null,
    active: true, applies_to: 'all', categories: [], product_ids: [], once_per_customer: false, exclude_sale_items: false,
    ...(coupon || {}),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const { id, status, used_count, created_at, ...body } = form;
    try {
      if (coupon) await axios.put(`${API}/admin/coupons/${coupon.id}`, body, withCreds);
      else await axios.post(`${API}/admin/coupons`, body, withCreds);
      onSaved();
    } catch (err) { setError(errMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={coupon ? 'Edit Coupon' : 'New Coupon'} onClose={onClose} width="max-w-2xl">
      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code *">
            <div className="flex gap-2">
              <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase().replace(/\s/g, ''))} required data-testid="coupon-code-input" className={`${inputCls} font-mono`} />
              <button type="button" title="Random code" onClick={() => set('code', randomCode())} className={btnSecondary}><Shuffle className="w-4 h-4" /></button>
            </div>
          </Field>
          <Field label="Discount">
            <div className="flex gap-2">
              <Select value={form.type} onChange={e => set('type', e.target.value)} className={selectCls}>
                <option value="percent">% off</option><option value="fixed">$ off order</option><option value="free_shipping">Free shipping</option>
              </Select>
              {form.type !== 'free_shipping' && (
                <input type="number" step="0.01" min="0" value={form.value} onChange={e => set('value', parseFloat(e.target.value) || 0)} className={inputCls} />
              )}
            </div>
          </Field>
          <Field label="Internal note" className="col-span-2">
            <input value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="e.g. Instagram giveaway" className={inputCls} />
          </Field>
          <Field label="Starts" hint="Blank = right away">
            <input type="datetime-local" value={toLocalInput(form.starts_at)} onChange={e => set('starts_at', fromLocalInput(e.target.value))} className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
          <Field label="Expires" hint="Blank = never">
            <input type="datetime-local" value={toLocalInput(form.expires_at)} onChange={e => set('expires_at', fromLocalInput(e.target.value))} className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
          <Field label="Minimum order ($)">
            <input type="number" step="0.01" min="0" value={form.min_order} onChange={e => set('min_order', parseFloat(e.target.value) || 0)} className={inputCls} />
          </Field>
          <Field label="Total uses allowed" hint="Blank = unlimited">
            <input type="number" min="1" value={form.max_uses ?? ''} onChange={e => set('max_uses', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
          </Field>
        </div>
        {form.type !== 'free_shipping' && (
          <Field label="Discounts">
            <div className="flex gap-2 mb-3">
              {[['all', 'Whole order'], ['categories', 'Categories'], ['products', 'Specific items']].map(([v, l]) => (
                <button type="button" key={v} onClick={() => set('applies_to', v)}
                  className={`px-4 py-2 rounded-xl border text-sm ${form.applies_to === v ? 'bg-honey/10 border-honey/40 text-honey-700' : 'bg-ink/[0.04] border-ink/10 text-ink/70'}`}>{l}</button>
              ))}
            </div>
            {form.applies_to === 'categories' && <CategoryPicker value={form.categories || []} onChange={v => set('categories', v)} />}
            {form.applies_to === 'products' && <ProductPicker value={form.product_ids || []} onChange={v => set('product_ids', v)} />}
          </Field>
        )}
        <div className="flex flex-wrap gap-6">
          {[['active', 'Enabled'], ['once_per_customer', 'One use per customer'], ['exclude_sale_items', "Doesn't apply to sale items"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} className="w-4 h-4 accent-honey" />
              <span className="text-ink/75 text-sm">{l}</span>
            </label>
          ))}
        </div>
        {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="save-coupon-btn" className={`${btnPrimary} flex-1`}>{saving ? 'Saving…' : 'Save coupon'}</button>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminCoupons() {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setCoupons((await axios.get(`${API}/admin/coupons`, withCreds)).data.coupons); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await axios.delete(`${API}/admin/coupons/${c.id}`, withCreds);
    load();
  };

  const rules = (c) => [
    c.applies_to === 'categories' ? (c.categories || []).join(', ') : c.applies_to === 'products' ? `${(c.product_ids || []).length} item(s)` : null,
    c.min_order ? `min $${c.min_order}` : null, c.once_per_customer ? 'once per customer' : null, c.exclude_sale_items ? 'not on sale items' : null,
  ].filter(Boolean).join(' · ');

  return (
    <AdminLayout>
      {editing && <CouponModal coupon={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <PageHeader title="Coupons" subtitle="Codes shoppers enter at checkout">
        <button onClick={() => setEditing('new')} data-testid="add-coupon-btn" className={btnPrimary}><Plus className="w-4 h-4" /> New Coupon</button>
      </PageHeader>
      <div className="admin-card rounded-xl overflow-x-auto">
        {loading ? <Spinner /> : coupons.length === 0 ? (
          <div className="text-center py-12"><Tag className="w-8 h-8 text-ink/30 mx-auto mb-2" /><p className="text-ink/45 text-sm">No coupons yet.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10">
              <tr className="text-ink/55 text-xs uppercase tracking-widest">
                <th className="text-left p-3">Code</th><th className="text-left p-3">Discount</th><th className="text-left p-3">Rules</th>
                <th className="text-left p-3">Used</th><th className="text-left p-3">Expires</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {coupons.map(c => (
                <tr key={c.id} className="text-ink/75 hover:bg-ink/[0.04]">
                  <td className="p-3"><span className="font-mono font-bold text-honey-700">{c.code}</span>{c.description && <p className="text-ink/45 text-xs">{c.description}</p>}</td>
                  <td className="p-3 text-honey-600 font-bold">{discountLabel(c.type, c.value)}</td>
                  <td className="p-3 text-xs text-ink/60">{rules(c) || '—'}</td>
                  <td className="p-3 text-xs">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                  <td className="p-3 text-xs text-ink/60">{c.expires_at ? fmtDateTime(c.expires_at) : 'Never'}</td>
                  <td className="p-3"><ScheduleBadge status={c.status} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button title="Advertise this coupon" onClick={() => navigate(`/admin/promotions?coupon=${c.code}`)} className="text-ink/55 hover:text-honey-600"><Megaphone className="w-4 h-4" /></button>
                      <button title="Edit" onClick={() => setEditing(c)} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>
                      <button title="Delete" onClick={() => remove(c)} className="text-ink/55 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
